import React, { useEffect, useState } from 'react';
import { useApolloClient, useQuery } from '@apollo/client';
import { LoggerFactory } from '@cvent/logging/LoggerFactory';
import useNotifications from '@hooks/useNotifications';
import useInterval from '@hooks/useInterval';
import NotificationNavigationSensor from '@components/notifications/NotificationNavigationSensor';
import { useNavigationSettings } from '@components/header/useEventNavigationSettings';
import { useAttendeeHubConfig } from '@hooks/useAttendeeHubConfig';
import { useEnrolledSessions } from '@context/EnrolledSessionsContext';
import useMeQuery from '@hooks/useMeQuery';
import useDebounce from '@hooks/useDebounce';
import updateSessions from '@tools/updateSessions';
import filterUpNextReminders from '@tools/filterUpNextReminders';
import { ENGAGEMENT_MESSAGE_TYPE, ENGAGEMENT_PANEL_IDS } from '@components/engagement/constants';
import useEngagementMessages from '@components/engagement/useEngagementMessages';
import { searchScheduleCriteria, getSessionQueryStart } from '@tools/getUpcomingSessionsSearchCriteria';
import { SEARCH_SCHEDULE_WITH_SPEAKERS } from '@graphql/operations/schedule/searchScheduleWithSpeakers';
import { useRouter } from 'next/router';

const LOG = LoggerFactory.create('Notifications');

interface NotificationsProps {
  eventId?: string;
  surveyUrl?: string;
  navHeight?: number;
  navHeaderVisible?: boolean;
}

/**
 * Component to determine if the NotificationNavigationSensor should be included in the navigation.
 * It reads the isPushNotificationsEnabled settings and the event's navigation settings
 *
 * @param {*} param0
 * @returns
 */
function Notifications({ eventId, surveyUrl, navHeight, navHeaderVisible }: NotificationsProps) {
  const { data } = useMeQuery();
  const apolloClient = useApolloClient();
  const { notifyMessage } = useEngagementMessages();
  const { hasMySchedule } = useNavigationSettings();
  const { isFeatureSettingEnabled } = useAttendeeHubConfig();
  const isPushNotificationsEnabled = isFeatureSettingEnabled('PLANNER_PUSH_NOTIFICATIONS');
  const router = useRouter();

  // TODO: Take a look at this hook. We are doing a lot but not sure if it is necessary if
  // push notifications are disabled
  const {
    notifications: notificationsList,
    reminders: remindersList,
    loading,
    markAndPublishNotificationsAsRead,
    dismissReminders,
    displayReminders,
    setApolloClient
  } = useNotifications();

  const [now, setNow] = useState(Date.now());
  // Checks at most every 60secs to see if we need to refresh the attendee's agenda for seamless transition toasts
  useInterval(() => setNow(Date.now()), 60000);

  const showAnnouncementNotificationBanner = data?.me?.allowPushNotifications ?? true;
  const showAppointmentNotificationBanner = data?.me?.allowAppointmentPushNotifications ?? true;

  const showNotificationBanner =
    showAnnouncementNotificationBanner === true || showAppointmentNotificationBanner === true;

  const { enrolledSessions } = useEnrolledSessions();

  // Non-debounced determination if we have pending reminders
  const hasPendingReminders = remindersList.filter(reminder => !reminder.dismiss).length > 0;
  // To smooth spikes, use a random delay (up to two minutes) before querying an attendee's schedule when
  // we receive approaching agenda item notifications via pubnub
  const [randomDelay] = useState(Math.floor(Math.random() * 120));
  const deboundedPendingReminders = useDebounce(hasPendingReminders, randomDelay * 1000);
  /**
   * This query is skiped if one of these conditions is met:
   * - event was not configured to have a publicLandingPage (self service events don't have MySchedule)
   * - seamless transitions are disabled
   * - all reminders have been dismissed
   */
  const { loading: attendeeScheduleLoading, data: { myAgenda: myAgendaResult } = {} } = useQuery(
    SEARCH_SCHEDULE_WITH_SPEAKERS,
    {
      onError: attendeeScheduleError => {
        LOG.error("Error retrieving attendee's schedule", {
          attendeeScheduleError
        });
      },
      variables: {
        criteria: searchScheduleCriteria(
          getSessionQueryStart(now, 5) // Use five minute interval to improve cache hits / reduce frequency of external queries
        )
      },
      skip:
        !hasMySchedule ||
        !hasPendingReminders || // Include non-debounced value to immediately skip when toasts are dismissed
        !deboundedPendingReminders
    }
  );

  // Param needed to determine if we happen to be within a session page
  const sessionIdFromRouter = router.query?.id;
  useEffect(() => {
    const shouldDisplayToast = upNextAgendaItems => {
      if (upNextAgendaItems.length) {
        if (upNextAgendaItems.length === 1 && upNextAgendaItems[0].id === sessionIdFromRouter) {
          // If the session id in the URL matches the id of the agenda item, don't show the toast
          // This basically prevents the session toast from showing up when the user is already on that session page
          return false;
        }
        return true;
      }
      return false;
    };

    // Short circuit.Self service events don't have the MySchedule button in the navigation
    if (!hasMySchedule) return;

    const upNextReminders = remindersList.filter(reminder => !reminder.dismiss);
    if (!attendeeScheduleLoading && upNextReminders.length > 0) {
      // Merge upcoming sessions with client-side enrollment
      const updatedSessions = updateSessions(myAgendaResult?.data, enrolledSessions);
      if (updatedSessions?.length > 0) {
        const { upNextAgendaItems, validReminderMessageIds, invalidReminderMessageIds } = filterUpNextReminders(
          upNextReminders,
          updatedSessions
        );

        displayReminders(validReminderMessageIds);
        dismissReminders(invalidReminderMessageIds);
        if (shouldDisplayToast(upNextAgendaItems)) {
          LOG.debug('Displaying seamless transition toasts', upNextAgendaItems);
          notifyMessage(ENGAGEMENT_PANEL_IDS.AGENDA_ITEM_REMINDERS, {
            msgType: ENGAGEMENT_MESSAGE_TYPE.TOAST,
            data: upNextAgendaItems,
            onDismiss: () => {
              dismissReminders(upNextReminders.map(reminder => reminder.messageId));
            }
          });
        }
      }
    }
  }, [
    remindersList,
    dismissReminders,
    displayReminders,
    myAgendaResult,
    attendeeScheduleLoading,
    enrolledSessions,
    notifyMessage,
    hasMySchedule,
    sessionIdFromRouter
  ]);

  // Need this to set apolloClient in Notification Provider
  // TODO remove this
  useEffect(() => {
    setApolloClient(apolloClient);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Dismiss actively displayed seamless transition toasts on unmount (page navigation)
  useEffect(
    () => () => {
      const displayedReminders = remindersList.filter(reminder => !reminder.dismiss && reminder.display);
      dismissReminders(displayedReminders.map(reminder => reminder.messageId));
    },
    [dismissReminders, remindersList]
  );

  if (!isPushNotificationsEnabled) return null;

  return (
    <NotificationNavigationSensor
      notificationList={notificationsList}
      eventId={eventId}
      allowAlertNotification={showNotificationBanner}
      notificationsLoading={loading}
      markAndPublishNotificationsAsRead={markAndPublishNotificationsAsRead}
      toastRedesignEnabled
      navHeight={navHeight}
      navHeaderVisible={navHeaderVisible}
      surveyUrl={surveyUrl}
    />
  );
}

export default Notifications;
